import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { useViewMode } from '../hooks/useViewMode';
import { aiApi, imagesApi, itemsApi, uploadApi } from '../utils/api';
import { AddImageButton } from '../components/shared/AddImageButton';
import { ImageDetailModal } from '../components/shared/ImageDetailModal';
import { SuccessModal } from '../components/shared/SuccessModal';
import { getAISettings } from '../utils/aiSettings';

interface Photo {
  id: string;
  url: string;
  filename: string;
  capturedAt: string;
  projectId?: string;
  projectName?: string;
  description?: string;
  tags?: string[];
  source?: 'item' | 'listing';
  sourceId?: string;
  voiceAnnotation?: string;
  itemPhotos?: Array<{
    imageData: string;
    detection: {
      class: string;
      confidence: number;
      boundingBox: {
        x: number;
        y: number;
        width: number;
        height: number;
      };
    };
    voiceAnnotation?: string;
  }>;
}

export const Photos: React.FC = () => {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [viewMode, setViewMode] = useViewMode('grid');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<'bulk' | Photo | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [creatingItems, setCreatingItems] = useState(false);
  
  // Success modal state
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successModalData, setSuccessModalData] = useState({
    title: '',
    message: '',
    icon: 'success' as 'success' | 'warning' | 'info'
  });
  
  // Image detail modal state
  const [selectedImage, setSelectedImage] = useState<Photo | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  
  
  // Full-screen viewer state
  const [fullscreenPhoto, setFullscreenPhoto] = useState<Photo | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [showControls, setShowControls] = useState(true);
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastTap, setLastTap] = useState(0);
  
  // Refs for touch handling
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loadPhotos = useCallback(async () => {
    try {
      setLoading(true);
      const response = await imagesApi.list();
      
      if (response.data.success) {
        console.log('Photos loaded:', response.data.photos);
        // Debug: Check if any photos have voice annotations
        const photosWithAnnotations = response.data.photos?.filter(p => p.voiceAnnotation);
        console.log('Photos with voice annotations:', photosWithAnnotations);
        setPhotos(response.data.photos || []);
      } else {
        console.error('Failed to load photos');
        setPhotos([]);
      }
    } catch (error: any) {
      console.error('Failed to load photos:', error);
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load photos from API
  useEffect(() => {
    loadPhotos();
  }, [loadPhotos]);

  // Listen for photo-added events to auto-refresh
  useEffect(() => {
    const handlePhotoAdded = () => {
      console.log('Photo added event received, reloading photos...');
      loadPhotos();
    };

    window.addEventListener('photo-added', handlePhotoAdded);
    
    return () => {
      window.removeEventListener('photo-added', handlePhotoAdded);
    };
  }, [loadPhotos]);

  const handlePhotoSelect = (photoId: string) => {
    const newSelected = new Set(selectedPhotos);
    if (newSelected.has(photoId)) {
      newSelected.delete(photoId);
    } else {
      newSelected.add(photoId);
    }
    setSelectedPhotos(newSelected);
    setShowBulkActions(newSelected.size > 0);
  };

  const handleImageClick = (photo: Photo) => {
    console.log('Opening image modal for photo:', photo);
    console.log('Photo voice annotation:', photo.voiceAnnotation);
    setSelectedImage(photo);
    setShowImageModal(true);
  };

  const handleCloseImageModal = () => {
    setShowImageModal(false);
    setSelectedImage(null);
  };

  const handleImageUpdated = (newImageUrl: string) => {
    // Refresh the photos list to show the updated image
    loadPhotos();
  };


  // Handle photo click - different logic for grid vs list view
  const handlePhotoClick = (photo: Photo, event: React.MouseEvent, isListView: boolean = false) => {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    const clickY = event.clientY - rect.top;
    
    let isCheckboxClick = false;
    
    if (isListView) {
      // In list view, checkbox is on the left side (first 40px width)
      isCheckboxClick = clickX <= 40;
    } else {
      // In grid view, checkbox is in top-left corner (48x48px area)
      isCheckboxClick = clickX <= 48 && clickY <= 48;
    }
    
    // If clicking on checkbox area, toggle selection
    if (isCheckboxClick) {
      handlePhotoSelect(photo.id);
      return;
    }
    
    // If photo is already selected and we have selections, toggle selection
    if (selectedPhotos.has(photo.id) && selectedPhotos.size > 0) {
      handlePhotoSelect(photo.id);
      return;
    }
    
    // If we have other photos selected, add this one to selection
    if (selectedPhotos.size > 0) {
      handlePhotoSelect(photo.id);
      return;
    }
    
    // Otherwise, open image detail modal
    handleImageClick(photo);
  };

  // Full-screen photo viewer functions
  const openFullscreen = (photo: Photo) => {
    const index = photos.findIndex(p => p.id === photo.id);
    setCurrentPhotoIndex(index);
    setFullscreenPhoto(photo);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    setShowControls(true);
    resetControlsTimeout();
  };

  const closeFullscreen = () => {
    setFullscreenPhoto(null);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
  };

  const navigatePhoto = (direction: 'prev' | 'next') => {
    const newIndex = direction === 'next' 
      ? (currentPhotoIndex + 1) % photos.length
      : (currentPhotoIndex - 1 + photos.length) % photos.length;
    
    setCurrentPhotoIndex(newIndex);
    setFullscreenPhoto(photos[newIndex]);
    setScale(1);
    setPosition({ x: 0, y: 0 });
    resetControlsTimeout();
    
  };

  const resetControlsTimeout = () => {
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      setShowControls(false);
    }, 3000);
  };

  const toggleControls = () => {
    setShowControls(!showControls);
    if (!showControls) {
      resetControlsTimeout();
    }
  };

  // Touch and gesture handling
  const handleImageTap = (e: React.TouchEvent | React.MouseEvent) => {
    const now = Date.now();
    const timeDiff = now - lastTap;
    
    if (timeDiff < 300 && timeDiff > 0) {
      // Double tap to zoom
      if (scale === 1) {
        setScale(2);
      } else {
        setScale(1);
        setPosition({ x: 0, y: 0 });
      }
    } else {
      // Single tap to toggle controls
      toggleControls();
    }
    setLastTap(now);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      setIsDragging(true);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (isDragging && e.touches.length === 1 && scale > 1) {
      const touch = e.touches[0];
      const rect = containerRef.current?.getBoundingClientRect();
      if (rect) {
        const x = touch.clientX - rect.left - rect.width / 2;
        const y = touch.clientY - rect.top - rect.height / 2;
        setPosition({ x: x / scale, y: y / scale });
      }
    }
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  // Swipe navigation
  const handleSwipe = (direction: 'left' | 'right') => {
    if (direction === 'left') {
      navigatePhoto('next');
    } else {
      navigatePhoto('prev');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      if (!fullscreenPhoto) return;
      
      switch (e.key) {
        case 'Escape':
          closeFullscreen();
          break;
        case 'ArrowLeft':
          navigatePhoto('prev');
          break;
        case 'ArrowRight':
          navigatePhoto('next');
          break;
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [fullscreenPhoto, currentPhotoIndex]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const handleSelectAll = () => {
    if (selectedPhotos.size === photos.length) {
      setSelectedPhotos(new Set());
      setShowBulkActions(false);
    } else {
      setSelectedPhotos(new Set(photos.map(p => p.id)));
      setShowBulkActions(true);
    }
  };

  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Only hide drag overlay if leaving the main container
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOver(false);
    }
  };

  const processImageFile = async (file: File): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        // Upload the image
        const formData = new FormData();
        formData.append('images', file);

        const uploadResponse = await uploadApi.uploadImages(formData);
        if (!uploadResponse.data.success || !uploadResponse.data.data?.files?.length) {
          throw new Error(`Upload failed for ${file.name}`);
        }

        const uploadedFile = uploadResponse.data.data.files[0];
        
        // Get image dimensions to calculate crop area
        const img = new Image();
        img.onload = async () => {
          try {
            // Calculate center crop area (4:3 aspect ratio)
            const targetRatio = 4 / 3;
            const imgRatio = img.width / img.height;
            
            let cropWidth, cropHeight;
            if (imgRatio > targetRatio) {
              // Image is wider - fit to height
              cropHeight = Math.min(img.height * 0.9, img.height);
              cropWidth = cropHeight * targetRatio;
            } else {
              // Image is taller - fit to width
              cropWidth = Math.min(img.width * 0.9, img.width);
              cropHeight = cropWidth / targetRatio;
            }
            
            const centerX = (img.width - cropWidth) / 2;
            const centerY = (img.height - cropHeight) / 2;
            
            const cropArea = {
              x: Math.round(Math.max(0, centerX)),
              y: Math.round(Math.max(0, centerY)),
              width: Math.round(Math.max(50, cropWidth)),
              height: Math.round(Math.max(50, cropHeight))
            };

            // Apply crop
            const cropResponse = await imagesApi.crop(uploadedFile.filename, cropArea);
            if (cropResponse.data.success) {
              console.log(`Successfully processed ${file.name}`);
            } else {
              console.warn(`Crop failed for ${file.name}, but upload succeeded`);
            }
            resolve();
          } catch (cropError) {
            console.warn(`Crop failed for ${file.name}:`, cropError);
            resolve(); // Still resolve since upload succeeded
          }
        };
        
        img.onerror = () => {
          console.warn(`Failed to load image for cropping: ${file.name}`);
          resolve(); // Still resolve since upload succeeded
        };
        
        // Set image source to trigger onload
        const photoUrl = `${process.env.REACT_APP_API_BASE_URL?.replace('/api', '') || ''}/uploads/${uploadedFile.filename}`;
        img.src = photoUrl;
        
      } catch (error) {
        console.error(`Failed to process ${file.name}:`, error);
        reject(error);
      }
    });
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(file => file.type.startsWith('image/'));

    if (imageFiles.length === 0) {
      alert('Please drop image files only.');
      return;
    }

    setUploading(true);
    setUploadProgress({ current: 0, total: imageFiles.length });

    try {
      // Process images sequentially to avoid overwhelming the server
      for (let i = 0; i < imageFiles.length; i++) {
        const file = imageFiles[i];
        setUploadProgress({ current: i + 1, total: imageFiles.length });
        
        try {
          await processImageFile(file);
        } catch (error) {
          console.error(`Failed to process ${file.name}:`, error);
          // Continue with other files even if one fails
        }
      }

      // Reload photos after all processing is complete
      await loadPhotos();
      
    } catch (error) {
      console.error('Drag and drop processing failed:', error);
      alert('Some images failed to upload. Please try again.');
    } finally {
      setUploading(false);
      setUploadProgress({ current: 0, total: 0 });
    }
  };

  const handleBulkCreateItems = async () => {
    if (selectedPhotos.size === 0) return;
    
    setCreatingItems(true);
    try {
      // Get AI settings
      const aiSettings = getAISettings();
      
      const photoIds = Array.from(selectedPhotos);
      const selectedPhotoObjects = photos.filter(photo => photoIds.includes(photo.id));
      
      let successCount = 0;
      let failedCount = 0;
      
      // Process each photo to create an item with AI analysis
      for (const photo of selectedPhotoObjects) {
        try {
          // Get AI analysis for the photo
          let aiEstimation = null;
          try {
            const requestData: any = {
              title: '',
              description: photo.voiceAnnotation || '',
              condition: 'used',
              materials: [],
              dimensions: {
                width: 0,
                height: 0,
                depth: 0,
                units: 'cm'
              }
            };

            // Add custom prompt if set
            if (aiSettings.customPrompt) {
              requestData.customPrompt = aiSettings.customPrompt;
            }

            // Add multiple items flag if set
            if (aiSettings.multipleItemsPerPhoto) {
              requestData.detectMultipleItems = true;
            }

            // Add voice annotation context if available
            if (photo.voiceAnnotation) {
              requestData.additionalContext = `Voice annotation from user: "${photo.voiceAnnotation}"`;
            }

            if (photo.url.startsWith('data:')) {
              const base64Data = photo.url.split(',')[1];
              requestData.base64Image = base64Data;
            } else {
              requestData.imageUrl = photo.url;
            }

            const aiResponse = await aiApi.estimate(requestData);
            if (aiResponse.data.success && aiResponse.data.data) {
              aiEstimation = aiResponse.data.data;
            }
          } catch (aiError) {
            console.warn('AI analysis failed for photo, using defaults:', aiError);
          }

          // Create item data from AI analysis or defaults
          const itemData: any = {
            title: aiEstimation?.recommendedTitle || photo.description || photo.filename || 'Untitled Item',
            description: aiEstimation?.notes || photo.description || '',
            images: [photo.url],
            condition: 'used' as const,
            price: aiEstimation?.estimatedValue || 0,
            currency: 'NZD',
            materials: aiEstimation?.attributes || [],
            status: 'draft' as const
          };

          // Include AI analysis data if available
          if (aiEstimation) {
            itemData.aiAnalysis = {
              estimatedValue: aiEstimation.estimatedValue,
              confidence: aiEstimation.confidence,
              notes: aiEstimation.notes,
              recommendedTitle: aiEstimation.recommendedTitle,
              attributes: aiEstimation.attributes,
              reasoning: aiEstimation.reasoning
            };
          }

          // Only include project if it's not null/undefined
          if (photo.projectId) {
            itemData.project = photo.projectId;
          }

          // Create the item
          const itemResponse = await itemsApi.create(itemData);
          if (itemResponse.data.success) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (error) {
          console.error('Failed to create item from photo:', photo.id, error);
          failedCount++;
        }
      }
      
      // Show results with proper modal
      if (successCount === 0) {
        setSuccessModalData({
          title: 'Creation Failed',
          message: 'Failed to create any items. Please try again or check your photos for AI analysis compatibility.',
          icon: 'warning'
        });
        setShowSuccessModal(true);
      } else if (failedCount > 0) {
        setSuccessModalData({
          title: 'Partial Success',
          message: `Successfully created ${successCount} item${successCount !== 1 ? 's' : ''} with AI analysis! However, ${failedCount} item${failedCount !== 1 ? 's' : ''} failed to create. Check the Items page to see your new items.`,
          icon: 'warning'
        });
        setShowSuccessModal(true);
      } else {
        setSuccessModalData({
          title: 'Items Created Successfully!',
          message: `Successfully created ${successCount} item${successCount !== 1 ? 's' : ''} with AI analysis! Your items are ready to be listed or edited.`,
          icon: 'success'
        });
        setShowSuccessModal(true);
      }
      
      // Clear selection
      setSelectedPhotos(new Set());
      setShowBulkActions(false);
      
    } catch (error: any) {
      console.error('Failed to create items from photos:', error);
      setSuccessModalData({
        title: 'Creation Failed',
        message: 'Failed to create items from photos. Please check your internet connection and try again.',
        icon: 'warning'
      });
      setShowSuccessModal(true);
    } finally {
      setCreatingItems(false);
    }
  };


  const handleBulkDelete = () => {
    setDeleteTarget('bulk');
    setShowDeleteConfirm(true);
  };

  const handleSinglePhotoDelete = (photo: Photo) => {
    setDeleteTarget(photo);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    setDeleting(true);
    try {
      if (deleteTarget === 'bulk') {
        // Bulk delete
        const selectedPhotosList = photos.filter(photo => selectedPhotos.has(photo.id));
        const filenames = selectedPhotosList.map(photo => photo.filename);
        
        const response = await imagesApi.deleteBatch(filenames);
        
        if (response.data.success) {
          // Reload photos from server to get updated list
          await loadPhotos();
          setSelectedPhotos(new Set());
          setShowBulkActions(false);
          
          // Show success message if some failed
          if (response.data.data?.failed && response.data.data.failed.length > 0) {
            console.warn('Some photos failed to delete:', response.data.data.failed);
          }
        }
      } else {
        // Single photo delete
        const photo = deleteTarget as Photo;
        const response = await imagesApi.delete(photo.filename);
        
        if (response.data.success) {
          // Close fullscreen if this photo was being viewed
          if (fullscreenPhoto?.id === photo.id) {
            closeFullscreen();
          }
          
          // Reload photos from server to get updated list
          await loadPhotos();
        }
      }
    } catch (error: any) {
      console.error('Failed to delete photos:', error);
      // You could add a toast notification here
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setDeleteTarget(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteTarget(null);
  };

  const handleAiGenerateImages = async () => {
    if (aiGenerating) return;
    
    setAiGenerating(true);
    try {
      // Generate the images using AI
      const response = await aiApi.generateImages(4);
      
      if (response.data.success && response.data.data?.images) {
        // Prepare images for saving to MongoDB as photos
        const imagesToSave = response.data.data.images.map((url, index) => ({
          url,
          title: `AI Generated Construction Photo ${index + 1}`,
          description: 'AI generated construction/demolition photo for testing'
        }));

        // Save the images to MongoDB as photos (not items)
        const saveResponse = await aiApi.saveGeneratedPhotos(imagesToSave);
        
        if (saveResponse.data.success) {
          // Reload photos to get the saved photos from the database
          await loadPhotos();
        } else {
          console.error('Failed to save AI photos to database');
        }
      }
    } catch (error) {
      console.error('Failed to generate AI images:', error);
      alert('Failed to generate AI images. Please try again.');
    } finally {
      setAiGenerating(false);
    }
  };


  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Group photos by project
  const groupedPhotos = React.useMemo(() => {
    const groups: { [key: string]: Photo[] } = {};
    
    photos.forEach(photo => {
      const key = photo.projectName || 'Unassigned';
      if (!groups[key]) {
        groups[key] = [];
      }
      groups[key].push(photo);
    });
    
    // Sort groups: projects first (alphabetically), then "Unassigned"
    const sortedGroupKeys = Object.keys(groups).sort((a, b) => {
      if (a === 'Unassigned') return 1;
      if (b === 'Unassigned') return -1;
      return a.localeCompare(b);
    });
    
    return sortedGroupKeys.map(groupName => ({
      name: groupName,
      photos: groups[groupName]
    }));
  }, [photos]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div 
      className="p-6 relative"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {/* Drag Overlay */}
      {dragOver && (
        <div className="fixed inset-0 bg-primary-500 bg-opacity-20 border-4 border-dashed border-primary-500 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-8 shadow-xl">
            <div className="text-center">
              <svg className="w-16 h-16 text-primary-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <h3 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Drop Images Here
              </h3>
              <p className="text-slate-600 dark:text-slate-400">
                Release to upload and process your images
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Upload Progress Overlay */}
      {uploading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white dark:bg-slate-800 rounded-lg p-8 shadow-xl max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
              <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                Uploading Images
              </h3>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Processing {uploadProgress.current} of {uploadProgress.total} images...
              </p>
              <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                <div 
                  className="bg-primary-500 h-2 rounded-full transition-all duration-300"
                  style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {/* Count removed as requested */}
        </div>

        <div className="flex items-center space-x-3">
          {/* Add Image Button */}
          <AddImageButton onImageAdded={loadPhotos} />
          
          {/* AI Test Button */}
          <button
            onClick={handleAiGenerateImages}
            disabled={aiGenerating}
            className={`btn-primary p-2 ${
              aiGenerating ? 'opacity-50 cursor-not-allowed' : ''
            }`}
            title="AI Test"
          >
            {aiGenerating ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            )}
          </button>
          

          {/* View Mode Toggle */}
          <div className="flex items-center space-x-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md transition-colors ${
                viewMode === 'list'
                  ? 'bg-white dark:bg-slate-700 shadow-sm'
                  : 'hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
          </div>

          {/* Select All Button (only show when photos are selected) */}
          {selectedPhotos.size > 0 && (
            <>
              <button
                onClick={handleSelectAll}
                className="btn-secondary"
              >
                {selectedPhotos.size === photos.length ? 'Deselect All' : 'Select All'}
              </button>
              <button
                onClick={() => {
                  setSelectedPhotos(new Set());
                  setShowBulkActions(false);
                }}
                className="btn-secondary"
              >
                Clear Selection
              </button>
            </>
          )}
        </div>
      </div>

      {/* Bulk Actions Bar */}
      {selectedPhotos.size > 0 && (
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium text-primary-700 dark:text-primary-300">
              Selected photos
            </span>
            <div className="flex items-center space-x-2">
              <button
                onClick={handleBulkCreateItems}
                disabled={creatingItems}
                className={`btn-primary text-sm ${creatingItems ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {creatingItems ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    Create Items
                  </>
                )}
              </button>
              <button
                onClick={handleBulkDelete}
                className="btn-destructive text-sm"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Photos Grid/List */}
      {viewMode === 'grid' ? (
        <div className="space-y-8">
          {groupedPhotos.map((group) => (
            <div key={group.name}>
              {/* Project Header */}
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center">
                  {group.name === 'Unassigned' ? (
                    <>
                      <svg className="w-5 h-5 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Unassigned Photos
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {group.name}
                    </>
                  )}
                  <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
                    ({group.photos.length} photo{group.photos.length !== 1 ? 's' : ''})
                  </span>
                </h2>
              </div>
              
              {/* Photos Grid */}
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                {group.photos.map((photo) => (
            <div
              key={photo.id}
              className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-200 ${
                selectedPhotos.has(photo.id)
                  ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-white dark:ring-offset-slate-900'
                  : 'hover:shadow-lg'
              }`}
              onClick={(e) => handlePhotoClick(photo, e, false)}
            >
              {/* Selection Checkbox */}
              <div className="absolute top-2 left-2 z-10">
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                  selectedPhotos.has(photo.id)
                    ? 'bg-primary-500 border-primary-500 scale-110'
                    : 'bg-white/90 border-white/90 backdrop-blur-sm hover:bg-white hover:scale-105'
                }`}>
                  {selectedPhotos.has(photo.id) && (
                    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>

              {/* Voice Annotation Indicator */}
              {photo.voiceAnnotation && (
                <div className="absolute top-2 right-2 z-10">
                  <div className="w-6 h-6 rounded-full bg-blue-500/90 border-2 border-white/90 backdrop-blur-sm flex items-center justify-center shadow-sm">
                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                  </div>
                </div>
              )}

              {/* Photo */}
              <div className="aspect-square bg-slate-200 dark:bg-slate-700">
                <img
                  src={photo.url}
                  alt={photo.description || photo.filename}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Overlay Info */}
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                <p className="text-white text-xs font-medium truncate">
                  {photo.description || photo.filename}
                </p>
                <p className="text-white/80 text-xs">
                  {formatDate(photo.capturedAt)}
                </p>
                {photo.projectName && (
                  <p className="text-primary-300 text-xs">
                    {photo.projectName}
                  </p>
                )}
              </div>
                </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-8">
          {groupedPhotos.map((group) => (
            <div key={group.name}>
              {/* Project Header */}
              <div className="mb-4">
                <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100 flex items-center">
                  {group.name === 'Unassigned' ? (
                    <>
                      <svg className="w-5 h-5 mr-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      Unassigned Photos
                    </>
                  ) : (
                    <>
                      <svg className="w-5 h-5 mr-2 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      {group.name}
                    </>
                  )}
                  <span className="ml-2 text-sm text-slate-500 dark:text-slate-400">
                    ({group.photos.length} photo{group.photos.length !== 1 ? 's' : ''})
                  </span>
                </h2>
              </div>
              
              {/* Photos List */}
              <div className="space-y-3">
                {group.photos.map((photo) => (
            <div
              key={photo.id}
              className={`flex items-center space-x-4 p-4 rounded-xl border transition-all duration-200 cursor-pointer ${
                selectedPhotos.has(photo.id)
                  ? 'border-primary-200 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20'
                  : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
              onClick={(e) => handlePhotoClick(photo, e, true)}
            >
              {/* Selection Checkbox */}
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-200 shadow-sm ${
                selectedPhotos.has(photo.id)
                  ? 'bg-primary-500 border-primary-500 scale-110'
                  : 'bg-white border-slate-300 dark:border-slate-600 dark:bg-slate-700 hover:scale-105'
              }`}>
                {selectedPhotos.has(photo.id) && (
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>

              {/* Thumbnail */}
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-slate-200 dark:bg-slate-700 flex-shrink-0">
                <img
                  src={photo.url}
                  alt={photo.description || photo.filename}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <h3 className="font-medium text-slate-900 dark:text-slate-100 truncate">
                  {photo.description || photo.filename}
                </h3>
                <p className="text-sm text-slate-600 dark:text-slate-400">
                  {formatDate(photo.capturedAt)}
                </p>
                {photo.projectName && (
                  <p className="text-sm text-primary-600 dark:text-primary-400">
                    {photo.projectName}
                  </p>
                )}
                {photo.tags && photo.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {photo.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-1 text-xs bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {photo.voiceAnnotation && (
                  <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-900/20 rounded border border-blue-200 dark:border-blue-800">
                    <div className="flex items-center space-x-1 mb-1">
                      <svg className="w-3 h-3 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400">Voice Note</span>
                    </div>
                    <p className="text-xs text-slate-700 dark:text-slate-300 italic line-clamp-2">
                      "{photo.voiceAnnotation}"
                    </p>
                  </div>
                )}
              </div>
                </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {photos.length === 0 && (
        <div className="text-center py-12">
          <svg className="w-16 h-16 text-slate-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
          <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No photos yet</h3>
          <p className="text-slate-600 dark:text-slate-400 mb-2">
            Upload photos by clicking "Add Image" or drag and drop images anywhere on this page.
          </p>
          <p className="text-slate-500 dark:text-slate-500 text-sm">
            Once uploaded, select photos and click "Create Items" to generate marketplace items with AI analysis.
          </p>
        </div>
      )}

      {/* Android-style Fullscreen Photo Viewer */}
      {fullscreenPhoto && (
        <div className="fixed inset-0 bg-black z-50 flex items-center justify-center">
          {/* Photo Container */}
          <div 
            ref={containerRef}
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img
              ref={imageRef}
              src={fullscreenPhoto.url}
              alt={fullscreenPhoto.description || fullscreenPhoto.filename}
              className="max-w-full max-h-full object-contain transition-transform duration-200 ease-out"
              style={{
                transform: `scale(${scale}) translate(${position.x}px, ${position.y}px)`,
                cursor: scale > 1 ? 'grab' : 'pointer'
              }}
              onClick={handleImageTap}
              onTouchEnd={handleImageTap}
              draggable={false}
            />

            {/* Navigation Arrows (Desktop) */}
            {!isMobile && photos.length > 1 && (
              <>
                <button
                  onClick={() => navigatePhoto('prev')}
                  className={`absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-200 ${
                    showControls ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                </button>
                <button
                  onClick={() => navigatePhoto('next')}
                  className={`absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-all duration-200 ${
                    showControls ? 'opacity-100' : 'opacity-0'
                  }`}
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </>
            )}
          </div>

          {/* Top Controls Bar */}
          <div className={`absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-4 transition-all duration-300 ${
            showControls ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full'
          }`}>
            <div className="flex items-center justify-between text-white">
              <button
                onClick={closeFullscreen}
                className="w-10 h-10 flex items-center justify-center rounded-full bg-black/30 hover:bg-black/50 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <div className="text-center">
                <p className="text-sm font-medium">
                  {currentPhotoIndex + 1} of {photos.length}
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handlePhotoSelect(fullscreenPhoto.id)}
                  className={`w-10 h-10 flex items-center justify-center rounded-full transition-colors ${
                    selectedPhotos.has(fullscreenPhoto.id)
                      ? 'bg-primary-500 text-white'
                      : 'bg-black/30 hover:bg-black/50 text-white'
                  }`}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </button>
                <button
                  onClick={() => handleSinglePhotoDelete(fullscreenPhoto)}
                  className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500/80 hover:bg-red-500 text-white transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Bottom Info Bar */}
          <div className={`absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 transition-all duration-300 ${
            showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-full'
          }`}>
            <div className="text-white">
              <h3 className="font-medium text-lg mb-1">
                {fullscreenPhoto.description || fullscreenPhoto.filename}
              </h3>
              <p className="text-sm text-white/80 mb-2">
                {formatDate(fullscreenPhoto.capturedAt)}
              </p>
              {fullscreenPhoto.projectName && (
                <p className="text-sm text-primary-300">
                  📁 {fullscreenPhoto.projectName}
                </p>
              )}
              {fullscreenPhoto.tags && fullscreenPhoto.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {fullscreenPhoto.tags.map((tag) => (
                    <span
                      key={tag}
                      className="px-2 py-1 text-xs bg-white/20 text-white rounded-full"
                    >
                      #{tag}
                    </span>
                  ))}
                </div>
              )}
              {fullscreenPhoto.voiceAnnotation && (
                <div className="mt-3 p-3 bg-blue-500/20 rounded-lg border border-blue-400/30">
                  <div className="flex items-center space-x-2 mb-2">
                    <svg className="w-4 h-4 text-blue-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    <span className="text-sm font-medium text-blue-300">Voice Note</span>
                  </div>
                  <p className="text-sm text-white/90 italic">
                    "{fullscreenPhoto.voiceAnnotation}"
                  </p>
                </div>
              )}
              
            </div>
          </div>

          {/* Zoom indicator */}
          {scale > 1 && (
            <div className="absolute top-20 right-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm">
              {Math.round(scale * 100)}%
            </div>
          )}

          {/* Swipe hint for mobile */}
          {isMobile && photos.length > 1 && showControls && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 text-white/60 text-sm">
              Swipe left or right to navigate
            </div>
          )}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
              Confirm Delete
            </h3>
            
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {deleteTarget === 'bulk' 
                ? `Are you sure you want to delete ${selectedPhotos.size} selected photo${selectedPhotos.size !== 1 ? 's' : ''}? This action cannot be undone.`
                : `Are you sure you want to delete "${(deleteTarget as Photo)?.description || (deleteTarget as Photo)?.filename}"? This action cannot be undone.`
              }
            </p>

            <div className="flex space-x-3">
              <button
                onClick={confirmDelete}
                disabled={deleting}
                className="btn-destructive flex-1 flex items-center justify-center"
              >
                {deleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </button>
              <button
                onClick={cancelDelete}
                disabled={deleting}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Detail Modal */}
      <ImageDetailModal
        isOpen={showImageModal}
        onClose={handleCloseImageModal}
        image={selectedImage}
        onImageUpdated={handleImageUpdated}
      />

      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        title={successModalData.title}
        message={successModalData.message}
        icon={successModalData.icon}
        actionText="View Items"
        actionPath="/items"
      />

    </div>
  );
};
